import { Link } from 'react-router-dom'
import { Shield, Lock, Eye, CheckCircle, AlertTriangle, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { SEOHead } from '@/components/SEOHead'

export default function SecurityPolicy() {
  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <SEOHead
        title="Security Policy | Sycek OSINT"
        description="Sycek OSINT security policy — our infrastructure security practices, data encryption standards, and responsible disclosure program."
        canonical="/security"
      />
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 via-purple-900/20 to-red-900/20"></div>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f61a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f61a_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

      {/* Header */}
      <div className="relative z-10 border-b border-cyan-500/30 bg-black/90 backdrop-blur-sm shadow-[0_4px_20px_rgba(34,211,238,0.15)]">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img 
              src="/logo.jpeg" 
              alt="Sycek Logo" 
              className="h-10 w-10 object-contain drop-shadow-[0_0_15px_rgba(34,197,94,0.6)]"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent drop-shadow-[0_0_10px_rgba(34,211,238,0.6)] font-mono">
                SYCEK OSINT
              </h1>
              <p className="text-xs text-green-400 font-mono drop-shadow-[0_0_5px_rgba(34,197,94,0.6)]">[SYSTEM ACTIVE]</p>
            </div>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-cyan-400 hover:bg-cyan-500/20 border border-cyan-500/50">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="w-8 h-8 text-green-400" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-green-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Security Policy
            </h1>
          </div>
          <p className="text-gray-400 text-sm font-mono">
            Our commitment to protecting your data and operations
          </p>
        </div>

        <Card className="bg-gray-900/50 border border-cyan-500/30 backdrop-blur-sm">
          <CardContent className="p-8 space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-4 flex items-center">
                <Lock className="w-6 h-6 mr-2" />
                Data Encryption
              </h2>
              <div className="text-gray-300 space-y-3">
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-green-400">TLS 1.3:</strong> All data in transit is encrypted using industry-standard TLS 1.3</li>
                  <li><strong className="text-green-400">AES-256:</strong> Data at rest encrypted with AES-256 encryption</li>
                  <li><strong className="text-green-400">Key Management:</strong> Encryption keys managed through secure key management systems</li>
                  <li><strong className="text-green-400">Certificate Pinning:</strong> SSL certificate pinning for enhanced security</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-4 flex items-center">
                <Eye className="w-6 h-6 mr-2" />
                Access Control
              </h2>
              <div className="text-gray-300 space-y-3">
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-green-400">Multi-Factor Authentication (MFA):</strong> Supported for all user accounts</li>
                  <li><strong className="text-green-400">Role-Based Access Control (RBAC):</strong> Granular permissions based on user roles</li>
                  <li><strong className="text-green-400">Single Sign-On (SSO):</strong> Enterprise SSO support available</li>
                  <li><strong className="text-green-400">Session Management:</strong> Secure session tokens with automatic expiration</li>
                  <li><strong className="text-green-400">Password Policies:</strong> Strong password requirements and regular rotation</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-4 flex items-center">
                <CheckCircle className="w-6 h-6 mr-2" />
                Security Monitoring
              </h2>
              <div className="text-gray-300 space-y-3">
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-green-400">24/7 Monitoring:</strong> Continuous security monitoring and threat detection</li>
                  <li><strong className="text-green-400">Intrusion Detection:</strong> Advanced IDS/IPS systems</li>
                  <li><strong className="text-green-400">Logging & Auditing:</strong> Comprehensive audit logs of all system activities</li>
                  <li><strong className="text-green-400">Anomaly Detection:</strong> AI-powered anomaly detection for suspicious activities</li>
                  <li><strong className="text-green-400">Incident Response:</strong> Dedicated security team for rapid incident response</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-4">Infrastructure Security</h2>
              <div className="text-gray-300 space-y-3">
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-green-400">Secure Data Centers:</strong> Tier III+ certified facilities with physical security</li>
                  <li><strong className="text-green-400">Network Security:</strong> Firewalls, DDoS protection, and network segmentation</li>
                  <li><strong className="text-green-400">Regular Backups:</strong> Encrypted daily backups with point-in-time recovery</li>
                  <li><strong className="text-green-400">Disaster Recovery:</strong> Comprehensive disaster recovery and business continuity plans</li>
                  <li><strong className="text-green-400">Vulnerability Management:</strong> Regular security assessments and penetration testing</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-4 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-2" />
                Security Best Practices
              </h2>
              <div className="text-gray-300 space-y-3">
                <p><strong className="text-cyan-400">For Users:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Enable multi-factor authentication on your account</li>
                  <li>Use strong, unique passwords</li>
                  <li>Regularly review your account activity and access logs</li>
                  <li>Report suspicious activities immediately</li>
                  <li>Keep your devices and browsers updated</li>
                  <li>Be cautious with sharing credentials or API keys</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-4">Compliance & Certifications</h2>
              <div className="text-gray-300 space-y-3">
                <p>We maintain compliance with:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-green-400">SOC 2 Type II:</strong> Annual security audits and certifications</li>
                  <li><strong className="text-green-400">ISO 27001:</strong> Information security management standards</li>
                  <li><strong className="text-green-400">GDPR:</strong> General Data Protection Regulation compliance</li>
                  <li><strong className="text-green-400">CCPA:</strong> California Consumer Privacy Act compliance</li>
                  <li><strong className="text-green-400">NIST Framework:</strong> Cybersecurity framework alignment</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-4">Security Incident Reporting</h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  If you discover a security vulnerability or incident, please report it immediately:
                </p>
                <div className="bg-gray-800/50 border border-red-500/30 rounded-lg p-4 font-mono">
                  <p><strong className="text-red-400">Security Email:</strong> security@sycek.io</p>
                  <p><strong className="text-red-400">Responsible Disclosure:</strong> We follow responsible disclosure practices</p>
                  <p><strong className="text-yellow-400">Response Time:</strong> Critical issues: 24 hours | High severity: 72 hours</p>
                </div>
                <p className="mt-4">
                  <strong className="text-yellow-400">Please include:</strong> Description of the issue, steps to reproduce, potential impact, and your contact information.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-4">Data Breach Response</h2>
              <div className="text-gray-300 space-y-3">
                <p>In the event of a data breach, we will:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Immediately contain and mitigate the breach</li>
                  <li>Assess the scope and impact</li>
                  <li>Notify affected users within 72 hours (as required by law)</li>
                  <li>Report to relevant data protection authorities</li>
                  <li>Provide transparent communication and remediation steps</li>
                  <li>Conduct post-incident review and implement improvements</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-4">Third-Party Security</h2>
              <div className="text-gray-300 space-y-3">
                <p>We carefully vet and monitor third-party services:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Security assessments of all vendors</li>
                  <li>Regular vendor security audits</li>
                  <li>Data processing agreements with security requirements</li>
                  <li>Continuous monitoring of third-party security posture</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-4">Contact Security Team</h2>
              <div className="text-gray-300 space-y-3">
                <div className="bg-gray-800/50 border border-cyan-500/30 rounded-lg p-4 font-mono">
                  <p><strong className="text-cyan-400">Security Team:</strong> security@sycek.io</p>
                  <p><strong className="text-cyan-400">General Inquiries:</strong> support@sycek.io</p>
                  <p><strong className="text-cyan-400">Address:</strong> Sycek OSINT, Bangalore KA, INDIA</p>
                </div>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

